/**
 * Restaurant Configuration für Eckpunkt App
 * Zentrale Konfiguration aller restaurant-spezifischen Daten
 *
 * Diese Datei kann einfach kopiert und für neue Restaurants angepasst werden
 */
// === ECKPUNKT RESTAURANT KONFIGURATION ===
export const restaurantConfig = {
    restaurant: {
        name: "Restaurant Eckpunkt",
        shortName: "Eckpunkt",
        projectId: "eckpunkt",
        description: "Gemütliches Restaurant mit regionaler Küche im Herzen der Stadt",
        cuisine: ["Deutsch", "Regional", "Saisonal"],
        contact: {
            phone: "+49 (0) 123 456 789",
            email: "info@restaurant-eckpunkt.de",
            website: "https://restaurant-eckpunkt.de"
        },
        address: {
            street: "Hauptstraße",
            number: "123",
            postalCode: "12345",
            city: "Musterstadt",
            country: "Deutschland",
            district: "Stadtzentrum"
        },
        openingHours: {
            monday: { open: "17:00", close: "22:00", isOpen: true },
            tuesday: { open: "17:00", close: "22:00", isOpen: true },
            wednesday: { open: "17:00", close: "22:00", isOpen: true },
            thursday: { open: "17:00", close: "22:00", isOpen: true },
            friday: { open: "17:00", close: "23:00", isOpen: true },
            saturday: { open: "12:00", close: "23:00", isOpen: true },
            sunday: { open: "12:00", close: "21:00", isOpen: true }
        },
        social: {
            facebook: "https://facebook.com/eckpunkt",
            instagram: "https://instagram.com/eckpunkt",
            google: "https://maps.google.com/place/eckpunkt"
        }
    },
    services: {
        delivery: {
            enabled: true,
            radius: 5,
            minimumOrder: 2000, // 20,00 €
            fee: 290, // 2,90 €
            freeDeliveryFrom: 3500 // 35,00 €
        },
        pickup: {
            enabled: true,
            minimumOrder: 1000 // 10,00 €
        },
        reservation: {
            enabled: true,
            maxAdvanceDays: 30
        }
    },
    pricing: {
        serviceFee: 50, // 0,50 €
        taxRate: 19, // 19%
        currency: "EUR",
        locale: "de-DE"
    },
    payment: {
        cash: true,
        card: true,
        paypal: true,
        bankTransfer: false
    },
    app: {
        theme: {
            primaryColor: "#2563eb", // blue-600
            secondaryColor: "#64748b", // slate-500
            accentColor: "#dc2626", // red-600
            markerColor: "#000000", // black für Marker
            mapType: "satellite" // Standard Karte
        },
        features: {
            loyaltyProgram: true,
            reviews: true,
            favorites: true,
            notifications: true
        },
        domains: {
            production: "eckpunkt.tastio.app",
            staging: "eckpunkt-staging.tastio.app",
            development: "localhost:3020"
        },
        api: {
            baseUrl: process.env.API_BASE_URL || "http://localhost:3030/api",
            publicUrl: process.env.PUBLIC_API_BASE_URL || "http://localhost:3030/public"
        }
    },
    legal: {
        businessName: "Eckpunkt Drink & Food",
        businessType: "private",
        vatNumber: "DE368020370",
        tradeLicenseNumber: "HRB 12345",
        owner: {
            name: "Max Mustermann",
            role: "Geschäftsführer"
        },
        imprint: "/impressum",
        privacy: "/datenschutz",
        terms: "/agb"
    },
    // === SEO & Marketing Konfiguration ===
    seo: {
        meta: {
            title: "Restaurant Eckpunkt - Regionale Küche in Musterstadt",
            description: "Genießen Sie regionale und saisonale Küche im Restaurant Eckpunkt. Gemütliche Atmosphäre, frische Zutaten und herzlicher Service im Herzen von Musterstadt.",
            keywords: [
                "Restaurant Eckpunkt",
                "Musterstadt Restaurant",
                "regionale Küche",
                "saisonale Küche",
                "deutsche Küche",
                "Restaurant Hauptstraße",
                "Online bestellen",
                "Lieferservice Musterstadt"
            ],
            author: "Restaurant Eckpunkt Team",
            robots: "index, follow",
            canonical: "https://eckpunkt.tastio.app"
        },
        openGraph: {
            title: "Restaurant Eckpunkt - Regionale Küche in Musterstadt",
            description: "Genießen Sie regionale und saisonale Küche im Restaurant Eckpunkt. Jetzt online bestellen oder reservieren!",
            image: "https://eckpunkt.tastio.app/images/og-image.jpg",
            imageAlt: "Restaurant Eckpunkt - Gemütliches Ambiente mit regionalen Spezialitäten",
            type: "restaurant",
            locale: "de_DE",
            siteName: "Restaurant Eckpunkt"
        },
        twitter: {
            card: "summary_large_image",
            title: "Restaurant Eckpunkt - Regionale Küche",
            description: "Regionale & saisonale Küche im Herzen von Musterstadt. Jetzt online bestellen!",
            image: "https://eckpunkt.tastio.app/images/twitter-card.jpg",
            creator: "@eckpunkt_restaurant",
            site: "@eckpunkt_restaurant"
        },
        structuredData: {
            name: "Restaurant Eckpunkt",
            description: "Gemütliches Restaurant mit regionaler und saisonaler Küche im Herzen von Musterstadt",
            image: [
                "https://eckpunkt.tastio.app/images/restaurant-exterior.jpg",
                "https://eckpunkt.tastio.app/images/restaurant-interior.jpg",
                "https://eckpunkt.tastio.app/images/signature-dish.jpg"
            ],
            telephone: "+49 (0) 123 456 789",
            email: "info@restaurant-eckpunkt.de",
            address: {
                streetAddress: "Hauptstraße 123",
                addressLocality: "Musterstadt",
                postalCode: "12345",
                addressCountry: "DE"
            },
            geo: {
                latitude: 51.5074,
                longitude: -0.1278
            },
            openingHours: [
                "Mo-Th 17:00-22:00",
                "Fr 17:00-23:00",
                "Sa 12:00-23:00",
                "Su 12:00-21:00"
            ],
            priceRange: "€€",
            servesCuisine: ["German", "Regional", "Seasonal"],
            paymentAccepted: ["Cash", "Credit Card", "PayPal"],
            hasMenu: "https://eckpunkt.tastio.app/speisekarte",
            orderUrl: "https://eckpunkt.tastio.app/bestellen"
        },
        analytics: {
            googleAnalyticsId: "G-5TB90Z7REF",
            googleTagManagerId: "GTM-XXXXXXX",
            facebookPixelId: "123456789012345",
            customScripts: [
                {
                    name: "Local Analytics",
                    src: "/scripts/local-analytics.js",
                    async: true,
                    defer: true
                }
            ]
        },
        tools: {
            generateSitemap: true,
            generateRobotsTxt: true,
            enableBreadcrumbs: true,
            enableRichSnippets: true
        }
    }
};
// Export als default für einfachen Import
export default restaurantConfig;
// Helper-Funktionen für häufig verwendete Daten
export const getRestaurantDisplayName = () => restaurantConfig.restaurant.name;
export const getContactPhone = () => restaurantConfig.restaurant.contact.phone;
export const getDeliveryInfo = () => restaurantConfig.services.delivery;
export const getThemeColors = () => restaurantConfig.app.theme;
export const getBusinessAddress = () => {
    const addr = restaurantConfig.restaurant.address;
    return `${addr.street} ${addr.number}, ${addr.postalCode} ${addr.city}`;
};
